import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import { useAuthStore } from '../../../../store/auth';
import { getMakeupAcademyPolicySummary, hasAnyRole } from '@court-sync/shared';
import { useUserScenario } from '../../../../hooks/useUserScenario';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useAcademySports } from '@/hooks/useAcademySports';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { lessonMatchesSportFilter } from '@/utils/publicBrowseMode';
import type { Lesson } from '@court-sync/shared';
import { Card, CardContent } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { StudentCreditsPage as StudentCreditsComponent } from '../../../../components/makeupCredits/StudentCreditsPage';
import { makeupcreditService, type CreditBalance, type CreditHistoryItem, type AvailableLessonForCredit, type AvailableTermLessonForCredit } from '../../../../services/makeupCredit';
import { lessonService } from '../../../../services/lesson';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { GraduationCap, Calendar } from 'lucide-react';

const creditsPageHelp = (
  <p>Manage your earned credits and book make-up lessons</p>
);

export default function StudentCreditsPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const { academyId: scenarioAcademyId } = useUserScenario();
  const academy = useGuardianAcademyStore((s) => s.academy);
  const { sports: academySports, isMultiSport } = useAcademySports(academy);
  const [sportFilter, setSportFilter] = useState('');
  const [lessonsById, setLessonsById] = useState<Record<string, Lesson>>({});

  const makeupPolicySummary = useMemo(
    () => (academy ? getMakeupAcademyPolicySummary(academy) : null),
    [academy]
  );

  const [byStudent, setByStudent] = useState<Array<{ studentId: string; studentName: string; availableCount: number; creditId?: string; expiresAt?: string; scope?: 'session' | 'term' }>>([]);
  const [creditBalance, setCreditBalance] = useState<CreditBalance>({
    available: 0,
    expiring_soon: 0,
    used: 0,
    expired: 0,
    total_earned: 0,
  });

  const [creditHistory, setCreditHistory] = useState<CreditHistoryItem[]>([]);
  const [pastEligibleLessons, setPastEligibleLessons] = useState<
    Array<{
      bookingId: string;
      lessonId: string;
      lessonName: string;
      lessonType: string;
      skillLevel?: string;
      date: string;
      notificationDeadlineHours: number;
      canReportAbsence: boolean;
    }>
  >([]);
  const [availableLessons, setAvailableLessons] = useState<AvailableLessonForCredit[]>([]);
  const [availableTermLessons, setAvailableTermLessons] = useState<AvailableTermLessonForCredit[]>([]);
  const [notificationDeadlineHours, setNotificationDeadlineHours] = useState(24);
  const [loading, setLoading] = useState(true);

  // Check role and fetch data
  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
      navigate('/dashboard');
      return;
    }

    fetchCreditsData();
  }, [user, navigate]);

  useEffect(() => {
    if (academy?.makeupNotificationDeadlineHours != null) {
      setNotificationDeadlineHours(academy.makeupNotificationDeadlineHours);
    }
  }, [academy?.makeupNotificationDeadlineHours]);

  const fetchCreditsData = async () => {
    try {
      setLoading(true);
      const academyId = scenarioAcademyId ?? user?.academyId;

      // Fetch credits overview (scoped by academy for multi-academy guardians)
      const overviewResponse = await makeupcreditService.getCreditsOverview(academyId);
      setCreditBalance(overviewResponse.balance);
      setCreditHistory(overviewResponse.history);
      setByStudent(overviewResponse.byStudent ?? []);

      // Fetch eligible lessons for absence reporting
      const eligibleResponse = await makeupcreditService.getPastEligibleLessons(academyId);
      setPastEligibleLessons(eligibleResponse);
      const storeAcademy = useGuardianAcademyStore.getState().academy;
      setNotificationDeadlineHours(
        storeAcademy?.makeupNotificationDeadlineHours ??
          (eligibleResponse.length > 0 ? eligibleResponse[0].notificationDeadlineHours : 24)
      );

      // Fetch available lessons for credit redemption (session credits)
      const availableResponse = await makeupcreditService.getAvailableLessonsForCredit(academyId);
      setAvailableLessons(availableResponse);
      if (academyId) {
        try {
          const lessonsRes = await lessonService.getLessons({ academyId });
          const map: Record<string, Lesson> = {};
          for (const lesson of lessonsRes.data ?? []) {
            map[lesson.id] = lesson;
          }
          setLessonsById(map);
        } catch {
          setLessonsById({});
        }
      }
      setAvailableTermLessons([]); // Term lessons fetched on demand when "Use Credit" clicked for term credit
    } catch (error) {
      console.error('Error fetching credits data:', error);
      toast.error('Failed to load credits data');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    return null;
  }

  const filteredAvailableLessons = sportFilter
    ? availableLessons.filter((row) => {
        const lesson = lessonsById[row.lessonId];
        if (!lesson) return true;
        return lessonMatchesSportFilter(lesson, sportFilter, academySports);
      })
    : availableLessons;

  if (loading) {
    return (
      <div className="contents">
        <PageHeader title="Make-up Credits" help={creditsPageHelp} />
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="contents">
      <PageHeader title="Make-up Credits" help={creditsPageHelp} />

      {isMultiSport ? (
        <div className="mb-4">
          <SportFilterBadges
            sports={academySports}
            value={sportFilter}
            onChange={setSportFilter}
            ariaLabel="Filter make-up lessons by sport"
          />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
        <DashboardStatCard
          icon={<GraduationCap className="text-purple-600 dark:text-purple-400" aria-hidden />}
          iconBg="bg-purple-100 dark:bg-purple-900"
          iconColor="text-purple-600 dark:text-purple-400"
          label="Available"
          value={creditBalance.available}
        />
        <DashboardStatCard
          icon={<Calendar className="text-amber-600 dark:text-amber-400" aria-hidden />}
          iconBg="bg-amber-100 dark:bg-amber-900"
          iconColor="text-amber-600 dark:text-amber-400"
          label="Expiring Soon"
          mobileLabel="Expiring"
          value={creditBalance.expiring_soon}
        />
        <DashboardStatCard
          icon={<GraduationCap className="text-green-600 dark:text-green-400" aria-hidden />}
          iconBg="bg-green-100 dark:bg-green-900"
          iconColor="text-green-600 dark:text-green-400"
          label="Used"
          value={creditBalance.used}
        />
        <div className="col-span-2 sm:col-span-1">
          <DashboardStatCard
            icon={<GraduationCap className="text-gray-600 dark:text-gray-400" aria-hidden />}
            iconBg="bg-gray-100 dark:bg-gray-800"
            iconColor="text-gray-600 dark:text-gray-400"
            label="Total Earned"
            mobileLabel="Earned"
            value={creditBalance.total_earned}
          />
        </div>
      </div>

      <StudentCreditsComponent
        creditBalance={creditBalance}
        creditHistory={creditHistory}
        byStudent={byStudent}
        availableTermLessonsForCredit={availableTermLessons}
        pastLessonsEligibleForAbsence={pastEligibleLessons.map((lesson) => ({
          booking: {
            id: lesson.bookingId,
            nextLessonDate: lesson.date,
          },
          lesson: {
            id: lesson.lessonId,
            name: lesson.lessonName,
            lessonType: lesson.lessonType,
            skillLevel: lesson.skillLevel,
          } as any,
        }))}
        availableLessonsForCredit={filteredAvailableLessons}
        notificationDeadlineHours={notificationDeadlineHours}
        onReportAbsenceSuccess={fetchCreditsData}
        onUseCreditsSuccess={fetchCreditsData}
        onUseCredit={async (cid, lessonId, sessionDate) => {
          try {
            await makeupcreditService.useCredit(cid, lessonId, sessionDate);
            toast.success('Make-up credit used successfully!');
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to use make-up credit');
            throw err;
          }
        }}
        onUseTermCredit={async (cid, lessonId) => {
          try {
            await makeupcreditService.useTermCredit(cid, lessonId);
            toast.success('Enrolled for the full term!');
            fetchCreditsData();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to use term credit');
            throw err;
          }
        }}
        onFetchAvailableLessons={async (creditId) => {
          const aid = scenarioAcademyId ?? user?.academyId;
          const lessons = await makeupcreditService.getAvailableLessonsForCredit(aid!, creditId);
          return lessons;
        }}
        onFetchAvailableTermLessons={async (creditId) => {
          const aid = scenarioAcademyId ?? user?.academyId;
          return makeupcreditService.getAvailableTermLessonsForCredit(creditId, aid ?? undefined);
        }}
        academyId={scenarioAcademyId ?? undefined}
        makeupPolicySummary={makeupPolicySummary}
      />

      {creditBalance.available === 0 && creditHistory.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex justify-center mb-4"><Calendar className="w-16 h-16 text-gray-400" /></div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Credits Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Report an absence to earn your first make-up credit. You'll have the opportunity to
              book a make-up lesson of the same type and skill level.
            </p>
            <button
              onClick={() => navigate(paths?.bookings ?? '/academy-picker')}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              View My Bookings
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
